import { PublicKey } from '@solana/web3.js';
import {
	getUserAccountPublicKeySync,
	getUserStatsAccountPublicKey,
	UserStats,
} from '@drift-labs/sdk';
import { COMMON_UI_UTILS } from '@drift/common';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useDriftClient from 'src/hooks/useDriftClient';
import { useCallback } from 'react';
import { dlog } from 'src/dev';

/* Sets deleted subaccounts' pubkeys, so users can still export the history. Returns false if there are none */
export const useSyncDeletedSubaccountsPubkey = () => {
	const driftClient = useDriftClient();
	const accountsSet = useDriftAccountsStore((s) => s.set);

	/**
	 * Sets deleted subaccounts' pubkeys, so users can still export the history. Returns false if there are none
	 */
	const syncDeletedSubaccountsPubkey = useCallback(
		(authority: PublicKey) => {
			const setNewDeletedKeys = (ids: number[]) => {
				accountsSet((s) => {
					s.deletedAccounts = ids.map((subAccountId) => {
						return {
							pubKey: getUserAccountPublicKeySync(
								driftClient?.program.programId as PublicKey,
								authority,
								subAccountId
							),
							userId: subAccountId,
							userKey: COMMON_UI_UTILS.getUserKey(subAccountId, authority),
							name: `Deleted Subaccount ${subAccountId}`,
						};
					});
				});
			};

			const createIdArray = (n: number): number[] =>
				Array.from({ length: n }, (_, i) => i);

			if (!authority || !driftClient) {
				return false;
			}

			try {
				// if userStats already set, check it for deleted accounts. if not, try to search for it
				if (driftClient.userStats && driftClient.userStats.isSubscribed) {
					const statsAccount = driftClient.userStats.getAccount();
					const numAccountsCreated = statsAccount.numberOfSubAccountsCreated;
					const numAccountsCurrent = statsAccount.numberOfSubAccounts;

					if (numAccountsCreated == numAccountsCurrent) {
						return false;
					}

					// if more created than current, then they have some deleted. find the missing subaccount ids from their user list
					const subAccountIds = Array.from(driftClient.users.values()).map(
						(user) => user.getUserAccount().subAccountId
					);

					const deletedIds = createIdArray(numAccountsCreated).filter(
						(num) => !subAccountIds.includes(num)
					);

					setNewDeletedKeys(deletedIds);

					return true;
				}

				const userStatsAccountPublicKey = getUserStatsAccountPublicKey(
					driftClient.program.programId,
					authority
				);

				const userStatsAccount = new UserStats({
					driftClient,
					userStatsAccountPublicKey,
					accountSubscription: { type: 'websocket' },
				});

				// If no user stats account, they truly never used Drift
				if (!userStatsAccount) {
					return false;
				}

				// Otherwise, they used Drift but deleted all accounts
				const accountsCreated =
					userStatsAccount.getAccount().numberOfSubAccountsCreated;

				setNewDeletedKeys(createIdArray(accountsCreated));

				return true;
			} catch (e) {
				dlog('deleted_accounts', `Error fetching deleted accounts: ${e}`);
				return false;
			}
		},
		[driftClient, accountsSet]
	);

	return { syncDeletedSubaccountsPubkey };
};
